import type { Entry, ActivityBurnSource } from "../types/entries";

/**
 * Fills missing body-weight values in entries from Apple HealthKit samples.
 *
 * Rules:
 *  - Only writes to an entry when entry.weight === null AND
 *    entry.weightSource is not "manual".
 *  - Sets weightSource = "healthkit" on every written entry.
 *  - Never overwrites a manually-entered weight.
 */
export function mergeHealthKitWeights(
  entries: Entry[],
  hkWeights: Array<{ date: string; kg: number }>,
  _activityBurnSource: ActivityBurnSource
): Entry[] {
  if (hkWeights.length === 0) return entries;

  const lookup = new Map<string, number>();
  for (const w of hkWeights) {
    lookup.set(w.date, w.kg);
  }

  return entries.map((entry) => {
    if (entry.weight !== null) return entry; // already has a value
    if (entry.weightSource === "manual") return entry; // manual lock
    const kg = lookup.get(entry.date);
    if (kg === undefined) return entry; // no HK data for this date
    return { ...entry, weight: kg, weightSource: "healthkit" as const };
  });
}

/**
 * Fills or overwrites activeCalories in entries from Apple HealthKit daily totals.
 *
 * Rules (activityBurnSource determines behaviour):
 *  - "manual"   → no-op (HealthKit not used for activity)
 *  - "oura"     → no-op (Oura preferred)
 *  - "healthkit"→ overwrite activeCalories unless activeCaloriesSource === "manual"
 *  - "best"     → only fill if activeCaloriesSource is null or "inferred"
 *                 (Oura data with source "oura" keeps priority)
 *
 * Never touches totalBurned — that field belongs exclusively to Oura.
 */
export function mergeHealthKitActiveCalories(
  entries: Entry[],
  hkCalories: Record<string, number>,
  activityBurnSource: ActivityBurnSource
): Entry[] {
  if (activityBurnSource === "manual" || activityBurnSource === "oura") {
    return entries;
  }

  const dateKeys = Object.keys(hkCalories);
  if (dateKeys.length === 0) return entries;

  return entries.map((entry) => {
    const kcal = hkCalories[entry.date];
    if (kcal === undefined) return entry; // no HK data for this date

    if (activityBurnSource === "healthkit") {
      // Overwrite unless manually entered
      if (entry.activeCaloriesSource === "manual") return entry;
      return {
        ...entry,
        activeCalories: kcal,
        activeCaloriesSource: "healthkit" as const,
      };
    }

    // activityBurnSource === "best"
    // Only fill gaps — Oura ("oura") and manual ("manual") take priority
    if (
      entry.activeCaloriesSource === null ||
      entry.activeCaloriesSource === undefined ||
      entry.activeCaloriesSource === "inferred"
    ) {
      return {
        ...entry,
        activeCalories: kcal,
        activeCaloriesSource: "healthkit" as const,
      };
    }

    return entry;
  });
}
